import type {QueryClient} from '@tanstack/react-query'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useMemo} from 'react'

import type {SystemColumnId} from '../../../api/columns/contracts/memex-column'
import type {GroupsAndGroupedItems, MemexItem, PaginatedMemexItems} from '../../../api/memex-items/contracts'
import {useEnabledFeatures} from '../../../hooks/use-enabled-features'
import {getQueryDataForMemexGroupsPage, setQueryDataForMemexGroupsPage} from '../query-client-api/memex-groups'
import {
  getQueryDataForGroupedItemsPage,
  getQueryDataForMemexItemsPage,
  setQueryDataForGroupedItemsPage,
  setQueryDataForMemexItemsPage,
} from '../query-client-api/memex-items'
import {isPageParamsDataGrouped, isQueryDataGrouped} from './query-data-helpers'
import {
  buildMemexGroupsQueryKey,
  buildMemexItemsOrGroupsQueryKey,
  createGroupedItemsId,
  createGroupedItemsPageType,
  createGroupedItemsPageTypeFromGroupedItemsId,
  isPageTypeForGroupedItems,
  type PageType,
  type PageTypeForGroupedItems,
  pageTypeForGroups,
  type PageTypeForUngroupedItems,
  pageTypeForUngroupedItems,
} from './query-keys'
import {
  type MemexGroupsPageQueryData,
  type MemexItemsPageQueryData,
  type MemexPageQueryData,
  type PageParam,
  pageParamForInitialPage,
  pageParamForNextPlaceholder,
  type PageParamsByGroupedItemsId,
  type PaginatedMemexItemsQueryVariables,
  type WithoutTotal,
} from './types'
import {useMemexItemsPageParams} from './use-memex-items-page-params'

// The next_placeholder should always be the last page param, so hasNextPage must be false.
export const nextPlaceholderPageInfo = {hasNextPage: false, hasPreviousPage: false}

/**
 * This hook provides utilities for continuing to display stale data for a view while
 * fresh data is being refetched from the server.
 *
 * The stale data is incrementally replaced by fresh data as new pages are returned from the server.
 * Additional pages are fetched until there are no more items on the server, or all stale items have
 * been replaced.
 *
 * Placeholder queries use `next_placeholder` for the pageParam component of their query keys.
 * The `next_placeholder` page param is always the last entry in the list of page params.
 *
 * A new request is triggered whenever a view's variables.fieldIds change. The optional
 * sourceFieldIds parameter allows us to seed this new query for the view with placeholder
 * data based on the previously visible fieldIds.
 *
 * @param {PaginatedMemexItemsQueryVariables} variables - the query variables for the relevant view
 * @param {ReadonlyArray<SystemColumnId | number> | undefined} sourceFieldIds - optional fieldIds to seed the placeholder query
 * @returns {useNextPlaceholderQueryReturnType}
 */
export function useNextPlaceholderQuery(
  variables: PaginatedMemexItemsQueryVariables,
  sourceFieldIds?: ReadonlyArray<SystemColumnId | number>,
): useNextPlaceholderQueryReturnType {
  const {getPageParamsQueryData, setPageParamsQueryData} = useMemexItemsPageParams(variables)
  const queryClient = useQueryClient()

  // Changing visible fieldIds will create a new query and trigger a new request.
  // We seed that query with placeholder data based on the previously visible fieldIds.
  const {memex_mwl_limited_field_ids} = useEnabledFeatures()
  const sourceVariables: PaginatedMemexItemsQueryVariables = useMemo(() => {
    if (memex_mwl_limited_field_ids) {
      return {...variables, fieldIds: sourceFieldIds ?? variables.fieldIds}
    }
    return variables
  }, [memex_mwl_limited_field_ids, sourceFieldIds, variables])
  const {getPageParamsQueryData: getSourcePageParamsQueryData} = useMemexItemsPageParams(sourceVariables)

  const setUpNextPlaceholderQueries = useCallback(() => {
    const placeholderPageParamsQueryData = getSourcePageParamsQueryData()
    if (isPageParamsDataGrouped(placeholderPageParamsQueryData)) {
      const nextGroupedItemsPageParams: PageParamsByGroupedItemsId = {}
      // Copy all items for each group into a placeholder query
      for (const [groupedItemsId, pageParams] of Object.entries(placeholderPageParamsQueryData.groupedItems)) {
        const pageType = createGroupedItemsPageTypeFromGroupedItemsId(groupedItemsId)
        // Build the placeholder query data using the source variables
        const nextPlaceholderPagesForGroupedItems = pageParams
          .flatMap(pageParam => getQueryDataForGroupedItemsPage(queryClient, sourceVariables, pageType, pageParam))
          .filter(page => !!page)
        const nextPlaceholderDataForGroupedItems: MemexItemsPageQueryData = {
          nodes: nextPlaceholderPagesForGroupedItems.flatMap(page => page.nodes),
          pageInfo: nextPlaceholderPageInfo,
        }
        // Set query data for the placeholder page for this group for the target variables
        setQueryDataForNextPlaceholder(queryClient, variables, pageType, nextPlaceholderDataForGroupedItems)

        // Set the updated pageParams metadata for this group: ['next_placeholder']
        nextGroupedItemsPageParams[groupedItemsId] = [pageParamForNextPlaceholder]
      }

      // Build a top-level group page placeholder query using the source variables
      const placeholderSourceData = placeholderPageParamsQueryData.pageParams.map(pageParam =>
        getQueryDataForMemexGroupsPage(queryClient, sourceVariables, pageParam),
      )
      const nextPlaceholderData: MemexGroupsPageQueryData = {
        groups: placeholderSourceData.flatMap(group => group?.groups ?? []),
        pageInfo: nextPlaceholderPageInfo,
      }
      // Set query data for the placeholder groups page for the target variables
      setQueryDataForNextPlaceholder(queryClient, variables, pageTypeForGroups, nextPlaceholderData)

      // Update the page params query data; top-level and per group:
      // groups: all groups, each with pageParams: ['next_placeholder']
      // pageParams: [undefined, 'next_placeholder']
      setPageParamsQueryData({
        groupedItems: nextGroupedItemsPageParams,
        pageParams: [pageParamForInitialPage, pageParamForNextPlaceholder],
      })
    } else {
      // Build a top-level items page placeholder query using the source variables
      const placeholderSourcePages = placeholderPageParamsQueryData.pageParams
        .map(pageParam => getQueryDataForMemexItemsPage(queryClient, sourceVariables, pageParam))
        .filter(page => !!page)
      // Move all data into the `afterPlaceholderData` query
      const nextPlaceholderData: MemexItemsPageQueryData = {
        nodes: placeholderSourcePages.flatMap(page => page.nodes),
        pageInfo: nextPlaceholderPageInfo,
      }
      // Set query data for the next placeholder page for the target variables
      setQueryDataForNextPlaceholder(queryClient, variables, pageTypeForUngroupedItems, nextPlaceholderData)
      // Update the page params query data to reflect the new page params: [undefined, 'next_placeholder']
      setPageParamsQueryData({pageParams: [pageParamForInitialPage, pageParamForNextPlaceholder]})
    }
  }, [getSourcePageParamsQueryData, queryClient, variables, setPageParamsQueryData, sourceVariables])

  const updateMemexItemsNextPlaceholderQuery = useCallback(
    (
      response: WithoutTotal<PaginatedMemexItems<MemexItem>>,
      pageType: PageTypeForGroupedItems | PageTypeForUngroupedItems,
    ) => {
      const currentPageParamsQueryData = getPageParamsQueryData()
      const isResultForGroup =
        isPageTypeForGroupedItems(pageType) && isPageParamsDataGrouped(currentPageParamsQueryData)
      const groupedItemsId = isResultForGroup ? createGroupedItemsId(pageType) : undefined
      const pageParams =
        isResultForGroup && groupedItemsId
          ? currentPageParamsQueryData.groupedItems[groupedItemsId] || []
          : currentPageParamsQueryData.pageParams
      if (pageParams[pageParams.length - 1] !== pageParamForNextPlaceholder) {
        // Nothing to handle, we can return early
        return
      }
      const newPageParams = getUpdatedItemsPageParams(queryClient, variables, pageParams, pageType, response)
      if (isResultForGroup && groupedItemsId) {
        const newGroups = {...currentPageParamsQueryData.groupedItems}
        newGroups[groupedItemsId] = newPageParams
        setPageParamsQueryData({
          ...currentPageParamsQueryData,
          groupedItems: newGroups,
        })
      } else {
        setPageParamsQueryData({
          ...currentPageParamsQueryData,
          pageParams: newPageParams,
        })
      }
    },
    [getPageParamsQueryData, queryClient, setPageParamsQueryData, variables],
  )

  const updateMemexGroupsNextPlaceholderQuery = useCallback(
    (response: WithoutTotal<GroupsAndGroupedItems<MemexItem>>) => {
      const currentPageParamsQueryData = getPageParamsQueryData()

      // First handle any placeholder data for each individual grouped items
      const originalGroupedItemsPageParams = isPageParamsDataGrouped(currentPageParamsQueryData)
        ? currentPageParamsQueryData.groupedItems
        : {}
      const newGroupedItemsPageParams = {...originalGroupedItemsPageParams}
      for (const groupedItems of response.groupedItems) {
        const pageType = createGroupedItemsPageType(groupedItems)
        const groupedItemsId = createGroupedItemsId(pageType)
        const pageParams = originalGroupedItemsPageParams[groupedItemsId] || []
        const updatedPageParams = getUpdatedItemsPageParams(queryClient, variables, pageParams, pageType, groupedItems)
        newGroupedItemsPageParams[groupedItemsId] = updatedPageParams
      }

      // This doesn't yet account for placeholder data for the secondary groups,
      // but we need to at least make sure that we build the initial page param
      // for the secondary groups if we loaded the data outside of the initial
      // JSON island.
      // We'll revisit this as part of https://github.com/github/projects-platform/issues/2017
      let newSecondaryGroups: Array<PageParam> | undefined = undefined
      if (response.secondaryGroups) {
        newSecondaryGroups = [pageParamForInitialPage]
      }

      // Then handle any placeholder data for the top level groups
      const pageParams = currentPageParamsQueryData.pageParams
      if (pageParams[pageParams.length - 1] === pageParamForNextPlaceholder) {
        // We have some data in the `next_placeholder` query, so lets update the data, and potentially
        // add a new query for the next page.
        const currentNextPlaceholderData = getQueryDataForMemexGroupsPage(
          queryClient,
          variables,
          pageParamForNextPlaceholder,
        )
        const pageParamsWithoutNextPlaceholder = pageParams.slice(0, pageParams.length - 1)
        const newNextPlaceholderDataGroups = currentNextPlaceholderData?.groups.slice(response.groups.nodes.length)
        if (
          newNextPlaceholderDataGroups?.length &&
          response.groups.pageInfo.hasNextPage &&
          response.groups.pageInfo.endCursor
        ) {
          const newNextPlaceholderData: MemexGroupsPageQueryData = {
            groups: newNextPlaceholderDataGroups,
            pageInfo: nextPlaceholderPageInfo,
          }
          const newPageParams: Array<PageParam> = [
            ...pageParamsWithoutNextPlaceholder,
            {after: response.groups.pageInfo.endCursor},
            pageParamForNextPlaceholder,
          ]

          setPageParamsQueryData({
            groupedItems: newGroupedItemsPageParams,
            pageParams: newPageParams,
            secondaryGroups: newSecondaryGroups,
          })

          // Set query data for the next placeholder page
          setQueryDataForMemexGroupsPage(queryClient, variables, pageParamForNextPlaceholder, newNextPlaceholderData)
        } else {
          // We have no more data in the next placeholder query, so let's remove it.
          queryClient.removeQueries({
            queryKey: buildMemexGroupsQueryKey(variables, pageParamForNextPlaceholder),
          })
          setPageParamsQueryData({
            groupedItems: newGroupedItemsPageParams,
            pageParams: pageParamsWithoutNextPlaceholder,
            secondaryGroups: newSecondaryGroups,
          })
        }
      } else {
        setPageParamsQueryData({
          ...currentPageParamsQueryData,
          groupedItems: newGroupedItemsPageParams,
          secondaryGroups: newSecondaryGroups,
        })
      }
    },
    [getPageParamsQueryData, queryClient, setPageParamsQueryData, variables],
  )

  return {setUpNextPlaceholderQueries, updateMemexGroupsNextPlaceholderQuery, updateMemexItemsNextPlaceholderQuery}
}

function setQueryDataForNextPlaceholder(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  pageType: PageType,
  nextPlaceholderData: MemexPageQueryData,
) {
  if (isQueryDataGrouped(nextPlaceholderData)) {
    setQueryDataForMemexGroupsPage(queryClient, variables, pageParamForNextPlaceholder, nextPlaceholderData)
  } else if (isPageTypeForGroupedItems(pageType)) {
    setQueryDataForGroupedItemsPage(queryClient, variables, pageType, pageParamForNextPlaceholder, nextPlaceholderData)
  } else {
    setQueryDataForMemexItemsPage(queryClient, variables, pageParamForNextPlaceholder, nextPlaceholderData)
  }
}

type useNextPlaceholderQueryReturnType = {
  /**
   * This function sets up "placeholder" queries for the current view.
   * Placeholder queries are used to display stale data while fresh data is being fetched.
   *
   * Calling this method will not trigger any server requests.
   */
  setUpNextPlaceholderQueries: () => void
  /**
   * This function checks if a placeholder query exists for the given items PageType:
   * - If it does, it updates that query based on the fresh items data in the response.
   * - If no placeholder exists, it does nothing.
   *
   * If the placeholder query contains more items than were returned from the server,
   * this function updates the view's page params to trigger a request for the next page.
   */
  updateMemexItemsNextPlaceholderQuery: (
    response: WithoutTotal<PaginatedMemexItems<MemexItem>>,
    pageType: PageTypeForGroupedItems | PageTypeForUngroupedItems,
  ) => void
  /**
   * This function checks if placeholder queries exist for the current grouped view:
   * - If it does, it updates those queries based on the fresh data in the response.
   * - If no placeholder exists, it does nothing.
   *
   * If the placeholder query contains more groups than were returned from the server,
   * this function updates the view's page params to trigger a request for the next page
   * of groups.
   */
  updateMemexGroupsNextPlaceholderQuery: (response: WithoutTotal<GroupsAndGroupedItems<MemexItem>>) => void
}

/**
 * As fresh items are returned from the server, this function updates the corresponding
 * placeholder query:
 * - It removes an equal number of stale items from the placeholder query as were returned from the server.
 * - If there are still items in the placeholder query, it updates the page params to fetch the next page.
 *
 * Once there are no more items in the placeholder query or there are no more items on the server,
 * it removes the placeholder query.
 *
 * @param {QueryClient} queryClient - the react-query client
 * @param {PaginatedMemexItemsQueryVariables} variables - the query variables for the relevant view
 * @param {Array<PageParam>} pageParams - the current page params metadata
 * @param {PageTypeForGroupedItems | PageTypeForUngroupedItems} pageType - the pageType for the fresh items data
 * @param {WithoutTotal<PaginatedMemexItems<MemexItem>>} newItemsData - a page of fresh items data
 *
 * @returns {Array<PageParam>} - the updated page params metadata.
 */
export const getUpdatedItemsPageParams = (
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  pageParams: Array<PageParam> = [],
  pageType: PageTypeForGroupedItems | PageTypeForUngroupedItems,
  newItemsData: WithoutTotal<PaginatedMemexItems<MemexItem>> | undefined,
): Array<PageParam> => {
  if (pageParams.length === 0 || pageParams[0] !== pageParamForInitialPage) {
    // A grouped response will include multiple groups with one initial page of items for each.
    // If the pageParams arg doesn't contain a "pageParamForInitialPage", then we can
    // infer that we're dealing with a grouped response, and we should seed the initial page param.
    pageParams.unshift(pageParamForInitialPage)
  }
  if (pageParams[pageParams.length - 1] !== pageParamForNextPlaceholder) {
    // Nothing else to handle, we can return early
    return pageParams
  }
  // We have some data in the `next_placeholder` query, so let's update the data, and potentially
  // add a new query for the next page.
  const currentNextPlaceholderData = isPageTypeForGroupedItems(pageType)
    ? getQueryDataForGroupedItemsPage(queryClient, variables, pageType, pageParamForNextPlaceholder)
    : getQueryDataForMemexItemsPage(queryClient, variables, pageParamForNextPlaceholder)
  const pageParamsWithoutNextPlaceholder = pageParams.slice(0, pageParams.length - 1)
  const newNextPlaceholderDataNodes = currentNextPlaceholderData?.nodes.slice(newItemsData?.nodes.length)
  if (newNextPlaceholderDataNodes?.length && newItemsData?.pageInfo.hasNextPage && newItemsData?.pageInfo.endCursor) {
    const newNextPlaceholderData: MemexItemsPageQueryData = {
      nodes: newNextPlaceholderDataNodes,
      pageInfo: nextPlaceholderPageInfo,
    }
    const newPageParams: Array<PageParam> = [
      ...pageParamsWithoutNextPlaceholder,
      {after: newItemsData?.pageInfo.endCursor},
      pageParamForNextPlaceholder,
    ]

    // Set query data for the next placeholder page
    if (isPageTypeForGroupedItems(pageType)) {
      setQueryDataForGroupedItemsPage(
        queryClient,
        variables,
        pageType,
        pageParamForNextPlaceholder,
        newNextPlaceholderData,
      )
    } else {
      setQueryDataForMemexItemsPage(queryClient, variables, pageParamForNextPlaceholder, newNextPlaceholderData)
    }
    return newPageParams
  } else {
    // We have no more data in the next placeholder query, so let's remove it.
    queryClient.removeQueries({
      queryKey: buildMemexItemsOrGroupsQueryKey(variables, pageType, pageParamForNextPlaceholder),
    })
    return pageParamsWithoutNextPlaceholder
  }
}
